import { useEffect, useRef } from "react";

/**
 * Observes a sentinel and loads more when the user scrolls it into view.
 * Does not auto-chain pages while the sentinel stays visible (short lists / tall viewports).
 */
export function useInfiniteScrollLoad({
  hasMore,
  loading,
  loadingMore,
  onLoadMore,
  rootRef,
  rootMargin = "120px",
}) {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  const wasIntersectingRef = useRef(false);
  const skipNextRef = useRef(false);
  const userScrolledRef = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      userScrolledRef.current = true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const root = rootRef?.current;
    root?.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      root?.removeEventListener("scroll", onScroll);
    };
  }, [rootRef, loading, loadingMore]);

  useEffect(() => {
    if (!loading) {
      skipNextRef.current = true;
      userScrolledRef.current = false;
      wasIntersectingRef.current = false;
    }
  }, [loading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || loadingMore) return undefined;

    const root = rootRef?.current ?? null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (skipNextRef.current) {
          skipNextRef.current = false;
          wasIntersectingRef.current = entry.isIntersecting;
          return;
        }

        const entered = entry.isIntersecting && !wasIntersectingRef.current;
        wasIntersectingRef.current = entry.isIntersecting;

        if (entered && userScrolledRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { root, rootMargin, threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, rootMargin, rootRef]);

  return sentinelRef;
}
